import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { createLogger } from '../lib/logger';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

const log = createLogger('error-boundary');

/**
 * Sprint 4 T4.5 — global error boundary.
 * Sprint 12 — Custel DS token'larına migration; dark mode-aware.
 * Sprint 17 T17.19 — structured logger.
 *
 * React render hatasında beyaz ekran yerine açıklayıcı durum gösterir;
 * Sentry SDK varsa hatayı capture eder.
 */
class ErrorBoundaryInner extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error('uncaught render error', error, { componentStack: info.componentStack });
    const sentry = (window as unknown as { Sentry?: { captureException: (e: Error) => void } }).Sentry;
    if (sentry?.captureException) sentry.captureException(error);
  }

  override render() {
    const { t } = this.props;
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-custel-bg p-6">
          <div className="w-full max-w-md rounded-custel border border-custel-danger/30 bg-custel-danger-subtle p-6 text-center shadow-custel-md">
            <h1 className="mb-2 text-lg font-semibold text-custel-danger">{t('errorBoundary.title')}</h1>
            <p className="mb-4 text-sm text-custel-text-secondary">{t('errorBoundary.description')}</p>
            <pre className="mb-4 max-h-40 overflow-auto rounded-custel-md border border-custel-border bg-custel-surface p-3 text-left text-xs text-custel-text">
              {this.state.error.message}
            </pre>
            <button onClick={() => window.location.reload()} className="btn-destructive">
              {t('errorBoundary.reload')}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const ErrorBoundary = withTranslation('components')(ErrorBoundaryInner);
